/**
 * /take/:token — Pre-screen page (Stories 13–18, 87, D5, 93, 94, 97, 98).
 *
 * Substrate (15-FG/39): **NOT inside <Shell>** — public credential surface.
 * Composes minimal chrome (wordmark header in full mode; suppressed in
 * embed mode) per `docs/ia/public-take.md` § Substrate compliance. Mirrors
 * `pages/attempts/result.tsx`'s `PublicResultChrome` pattern from 15-FG/32.
 *
 * Modes:
 *   - Full mode (`/take/:token`) → wordmark header on top, cream `bg`
 *     background, editorial layout.
 *   - Embed mode (`/take/:token/embed` OR `/take/:token?embed=1`) → header
 *     suppressed, page background shifts to `bg-card` to blend into the
 *     host iframe (sandbox lines 73–74 — `.header{display:none}`).
 *
 * Renders:
 *   - Task Brief (markdown) — Story 14
 *   - Estimated Minutes badge — Story 16
 *   - Task Artefact download button — Story 15
 *   - Candidate fields (name + email, embedded mode) + consent checkbox —
 *     Stories 13/18 (Authenticated cookies skip the form fields)
 *   - Privacy notice (embed mode only, Story D5) — non-dismissable one-liner
 *     "Your name, email, and answers will be shared with [Company] for hiring evaluation"
 *   - "Begin Test" submit button — Story 18
 *   - Error states (token invalid / expired / consumed / quota / lockout) —
 *     Story 87
 *
 * Embed mode (11-iframe-embed/02):
 *   - Triggered by `?embed=1` query param OR `/take/:token/embed` route segment.
 *   - Minimal layout: no nav sidebar, compact single-column, fits iframe.
 *   - After successful begin, stays within the iframe — navigates to
 *     /attempt/:id (question phase placeholder).
 *   - Privacy notice required per Story D5.
 *
 * On Begin Test success the page navigates to the question-phase URL
 * (placeholder until 06-attempt-engine/02 ships).
 *
 * No markdown library is bundled yet (would be a Module-10 design-tokens
 * decision); we render the brief as `<pre>` so it's readable without
 * adding a dependency this issue can't ratify.
 *
 * Cross-cutting invariants (15-FG/39):
 *   - Inv 12 (no console.log) — none introduced.
 *   - Inv 13 (no `any` / @ts-ignore) — none.
 *   - Inv 14 (internal nav via <Link>) — wordmark is a Link to "/".
 *   - Inv 15 (atoms from barrel) — Heading / Eyebrow / Button / Input / Link
 *     composed from `@/components/ui`.
 */

import { useEffect, useState, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Input, Link } from '@/components/ui';
import { INTEGRITY_PLEDGE_VERSION, INTEGRITY_PLEDGE_BODY } from '@bimassess/db/legal-versions';
import {
  beginAttempt,
  getPreScreen,
  type AttemptErrorEnvelope,
  type PreScreenView,
} from '../../state/attempt-engine/api.ts';
import { AvailabilityBanner } from '@/components/availability/AvailabilityBanner';
import { deriveAvailability } from '../../lib/availability';

const API_BASE = '';

function humanError(err: AttemptErrorEnvelope): string {
  switch (err.code) {
    case 'token_invalid':
      switch (err.reason) {
        case 'expired':
          return 'This assessment link has expired. Please request a new one from your administrator.';
        case 'consumed':
          return 'This assessment link has already been used and cannot be reused.';
        case 'revoked':
          return 'This assessment link was revoked. Please request a new one from your administrator.';
        case 'unknown':
        case 'malformed':
        default:
          return 'This assessment link is not recognised. Please check the URL and try again.';
      }
    case 'quota_exceeded':
      return 'The Company assigned to this assessment has used all of its allotted attempts. Please contact your administrator.';
    case 'lockout_active':
      return `You recently completed this assessment and the resit lockout is active until ${err.locked_until ?? 'a later time'}.`;
    case 'embed_origin_rejected':
      return 'This assessment can only be taken from an approved site.';
    case 'unauthorized':
      return 'You need to sign in before starting this assessment.';
    case 'validation':
      return `${err.field ? `${err.field}: ` : ''}${err.message}`;
    case 'conflict':
      return 'You already have an in-progress attempt for this assessment. Please resume it instead of starting a new one.';
    default:
      return err.message || 'Something went wrong starting this assessment.';
  }
}

// ─── Minimal public chrome (NOT Shell) ────────────────────────────────────────

/**
 * Wordmark header for the public take surface in full mode.
 *
 * Renders a left-aligned "BIMAssess" wordmark linking to "/" (catalogue
 * fallback) — the only internal nav the public pre-screen exposes. No
 * catalogue / dashboard / data nav, no user menu — this is deliberately
 * not the authed-app shell.
 *
 * In embed mode this component is not rendered at all (sandbox lines 73–74
 * — `.header{display:none}`) so the page blends into a host iframe.
 */
function PublicTakeChrome(): ReactElement {
  return (
    <header role="banner" className="border-b border-line bg-card">
      <div className="mx-auto flex max-w-page items-center justify-between px-6 py-5">
        <Link
          to="/"
          aria-label="BIMAssess"
          className="font-sans text-lg font-medium tracking-tight text-ink no-underline hover:text-accent"
        >
          BIMAssess
        </Link>
      </div>
    </header>
  );
}

interface PageFrameProps {
  embed: boolean;
  children: ReactNode;
}

/**
 * Page-level frame for the public take surface.
 *
 * Full mode renders the wordmark header + cream page background; embed
 * mode renders no header and shifts the background to `bg-card` (white)
 * to blend into a host page (matches `pages/attempts/result.tsx` Polish 10).
 *
 * The inner container is intentionally a thin wrapper — the body content
 * supplies its own `max-w-3xl` editorial width per the prescreen layout.
 */
function PageFrame({ embed, children }: PageFrameProps): ReactElement {
  const bg = embed ? 'bg-card' : 'bg-bg';
  return (
    <div className={`min-h-screen ${bg}`}>
      {embed ? null : <PublicTakeChrome />}
      <main role="main">{children}</main>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export interface PreScreenPageProps {
  /** Embed mode suppresses chrome (wordmark header). Default false. */
  embed?: boolean;
}

export function PreScreenPage({ embed = false }: PreScreenPageProps = {}): ReactElement {
  const params = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = params.token ?? '';
  // Embed mode resolves via EITHER the route segment (prop=true) OR the legacy
  // `?embed=1` query param. Both flip the same boolean so the API gating in
  // getPreScreen + the chrome suppression stay in lock-step.
  const isEmbed = embed || searchParams.get('embed') === '1';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PreScreenView | null>(null);
  const [loadError, setLoadError] = useState<AttemptErrorEnvelope | null>(null);
  const [submitError, setSubmitError] = useState<AttemptErrorEnvelope | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [consent, setConsent] = useState(false);
  // J-X20 — integrity pledge attestation. Starts UNCHECKED; Begin Test
  // disabled until checked AND `consent` is checked. Version stamped server-side
  // from INTEGRITY_PLEDGE_VERSION at Begin Test (route layer).
  const [pledged, setPledged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      const res = await getPreScreen(API_BASE, token, { embed: isEmbed });
      if (cancelled) return;
      if (!res.ok) {
        setLoadError(res.error);
      } else {
        setData(res.data);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, isEmbed]);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!data) return;
    setSubmitError(null);
    setSubmitting(true);
    const isEmbedded = data.token_mode === 'embedded';
    const res = await beginAttempt(API_BASE, {
      share_token: token,
      ...(isEmbedded
        ? {
            candidate_name: candidateName.trim(),
            candidate_email: candidateEmail.trim(),
          }
        : {}),
      // J-X20 — pledge state submitted at Begin Test. Route layer validates
      // accepted=true and stamps the version onto the attempts row.
      integrity_pledge_accepted: pledged,
      integrity_pledge_version: INTEGRITY_PLEDGE_VERSION,
    });
    setSubmitting(false);
    if (!res.ok) {
      setSubmitError(res.error);
      return;
    }
    // Write begin result to sessionStorage so the question-phase page can
    // hydrate without a second network round-trip (sessionStorage key matches
    // what question-phase.tsx reads at line ~443).
    sessionStorage.setItem(`attempt:${res.data.attempt_id}`, JSON.stringify(res.data));
    // Navigate to the question-phase URL.
    // In embed mode, navigation stays within the iframe — no top-level redirect.
    navigate(`/attempt/${res.data.attempt_id}`);
  }

  // Body inner-container width: embed mode stays compact for iframe; full mode
  // uses the editorial-3xl column the contract describes ("start screen reads
  // like the cover page of a printed exam booklet").
  const containerClass = isEmbed
    ? 'px-4 py-6'
    : 'mx-auto max-w-3xl px-6 py-12';

  if (loading) {
    return (
      <PageFrame embed={isEmbed}>
        <div className={`${containerClass} font-mono text-mono text-muted`}>
          Loading assessment…
        </div>
      </PageFrame>
    );
  }

  if (loadError) {
    return (
      <PageFrame embed={isEmbed}>
        <div className={containerClass}>
          <p className="font-mono text-eyebrow uppercase text-muted-2">
            Assessment unavailable
          </p>
          <h1 className="mt-3 font-sans text-h2 font-medium tracking-tight">
            {humanError(loadError)}
          </h1>
        </div>
      </PageFrame>
    );
  }

  if (!data) {
    return (
      <PageFrame embed={isEmbed}>
        <div className={`${containerClass} text-muted`}>
          Assessment data not available.
        </div>
      </PageFrame>
    );
  }

  const isEmbedded = data.token_mode === 'embedded';

  // J-X22 — derive availability state from the test's window fields. Used to
  // show AvailabilityBanner and to block Begin Test when the window is closed.
  const availInput = {
    availability_starts_at: data.test.availability_starts_at ?? null,
    availability_ends_at: data.test.availability_ends_at ?? null,
    availability_closed_reason: data.test.availability_closed_reason ?? null,
  };
  const availState = deriveAvailability(availInput);
  const isTestClosed = availState.kind === 'closed';

  return (
    <PageFrame embed={isEmbed}>
      <div className={containerClass}>
        {/* J-X22 — Availability banner (closing-soon warning or closed block). */}
        <AvailabilityBanner state={availState} />

        {/* In embed mode, skip the eyebrow — keep it compact */}
        {!isEmbed && (
          <p className="font-mono text-eyebrow uppercase text-muted-2">
            Pre-screen — read carefully
          </p>
        )}
        <h1 className={`font-sans font-medium tracking-tight ${isEmbed ? 'text-h1 mt-0' : 'mt-3 text-h2'}`}>
          {data.test.title}
        </h1>

        <div className="mt-4 inline-flex items-center gap-3 rounded-md border border-line bg-bg-warm px-4 py-2">
          <span aria-hidden className="inline-block size-1.5 rounded-pill bg-accent" />
          <span className="font-mono text-mono text-muted">
            Estimated time:&nbsp;
            <strong className="text-ink">{data.test.estimated_minutes} min</strong>
          </span>
          <span className="font-mono text-mono text-muted-2">
            · Test limit: {data.test.time_limit} min
          </span>
        </div>

        {data.test.task_brief_md !== null && data.test.task_brief_md.length > 0 ? (
          <section className="mt-6">
            <h2 className="font-sans text-h2 font-medium">Task brief</h2>
            <pre className="mt-3 whitespace-pre-wrap rounded-md border border-line bg-bg-warm p-4 font-sans text-body text-ink">
              {data.test.task_brief_md}
            </pre>
          </section>
        ) : null}

        {data.test.task_artifact_url !== null &&
        data.test.task_artifact_url.length > 0 ? (
          <section className="mt-4">
            <a
              href={data.test.task_artifact_url}
              download
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-line bg-bg px-4 py-3 font-mono text-mono text-ink hover:border-accent"
            >
              ↓ Download task artefact
            </a>
            <p className="mt-2 font-mono text-mono text-muted-2">
              Open this in Revit on your own machine and complete the task before clicking Begin Test.
            </p>
          </section>
        ) : null}

        <form className="mt-8 space-y-5" onSubmit={onSubmit} noValidate>
          {isEmbedded ? (
            <>
              <label className="block">
                <span className="font-mono text-mono text-muted">Your name</span>
                <Input
                  type="text"
                  required
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  className="mt-2 w-full rounded-md border border-line bg-bg px-4 py-3 font-sans text-body text-ink"
                />
              </label>
              <label className="block">
                <span className="font-mono text-mono text-muted">Your email</span>
                <Input
                  type="email"
                  required
                  value={candidateEmail}
                  onChange={(e) => setCandidateEmail(e.target.value)}
                  className="mt-2 w-full rounded-md border border-line bg-bg px-4 py-3 font-sans text-body text-ink"
                />
              </label>

              {/* Story D5: Privacy notice — non-dismissable, one-line with link.
                  Company name resolved from share token's company_id by the server. */}
              <p className="rounded-md border border-line bg-bg-warm px-4 py-3 font-mono text-mono text-muted text-sm" role="note" aria-label="Privacy notice">
                Your name, email, and answers will be shared with{' '}
                <strong className="text-ink">{data.company_name ?? 'the hiring company'}</strong>{' '}
                for hiring evaluation.{' '}
                <a
                  href="/legal/privacy"
                  className="underline hover:text-accent"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy policy
                </a>
              </p>
            </>
          ) : null}

          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              required
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1.5 size-4"
            />
            <span className="font-sans text-body text-ink">
              I understand that clicking Begin Test starts the timer and I will not be able to pause it.
            </span>
          </label>

          {/* J-X20 — Integrity pledge block. Renders ABOVE the Begin button.
              Checkbox starts UNCHECKED (pre-checking invalidates explicit
              consent — Failure mode 3). Begin button is disabled until both
              `consent` and `pledged` are true. */}
          <section
            aria-labelledby="integrity-pledge-heading"
            className="rounded-md border border-line bg-bg-warm p-5"
          >
            <h2
              id="integrity-pledge-heading"
              className="font-sans text-h3 font-medium tracking-tight"
            >
              Before you begin
            </h2>
            <div className="mt-3 whitespace-pre-line font-sans text-body text-ink">
              {INTEGRITY_PLEDGE_BODY}
            </div>
            <label className="mt-5 flex items-start gap-3">
              <input
                type="checkbox"
                required
                aria-required
                checked={pledged}
                onChange={(e) => setPledged(e.target.checked)}
                className="mt-1.5 size-4"
                data-testid="integrity-pledge-checkbox"
              />
              <span className="font-sans text-body text-ink">
                I have read and agree to the above statement.
              </span>
            </label>
            <p className="mt-3 font-mono text-mono text-muted-2">
              Pledge version {INTEGRITY_PLEDGE_VERSION}
            </p>
          </section>

          {submitError !== null ? (
            <p
              role="alert"
              className="rounded-md border border-line bg-bg-warm px-4 py-3 font-mono text-mono text-ink"
            >
              {humanError(submitError)}
            </p>
          ) : null}

          <Button
            variant="primary"
            type="submit"
            disabled={!consent || !pledged || submitting || isTestClosed}
            aria-disabled={!consent || !pledged || isTestClosed}
          >
            {submitting ? 'Starting…' : 'Begin Test →'}
          </Button>
        </form>
      </div>
    </PageFrame>
  );
}
